import {
  ActionOverRecordTypes,
  createFormModalConfig,
  createLocalizationKey,
  FieldBuilder,
  FieldDefinition,
  IActionsSchema,
  IModal,
  MENU_CATEGORY_KEY,
  MENU_NAME_SUFFIX,
  MENU_SUBCATEGORY_KEY,
  MenuCategories,
  MenuSubcategories,
  modalAction,
  triggerAction,
  ZendraLocalizationServices,
} from '@zendra/shared';
import { CostReceiverService, LocationService, PeriodService, ReasonService, StandAreaMaintenanceTypeService } from 'cbd-proxies';
import {
  CycleProjectionPlanningOrchestratorService,
  RotationProjectionPlanningOrchestratorService,
} from 'orchestrator';
import { ActionCreateStandAreaMaintenance } from './action-create-stand-area-maintenance/action-create-stand-area-maintenance';

FieldBuilder.setNamespace('Geographic');

export const STANDAREAMAINTENANCE_FIELDS: FieldDefinition[] = [
  FieldBuilder.number(
    'standAreaMaintenanceId',
    'Entity.StandAreaMaintenance.StandAreaMaintenanceId',
  )
    .readOnly()
    .build(),
  FieldBuilder
    .number('currentArea', 'Entity.StandAreaMaintenance.CurrentArea')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .number('gisArea', 'Entity.StandAreaMaintenance.GisArea')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .number('areaToBeAdjusted', 'Entity.StandAreaMaintenance.AreaToBeAdjusted')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .number('differenceInPercentage', 'Entity.StandAreaMaintenance.DifferenceInPercentage')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .checkbox('isAreaUpdated', 'Entity.StandAreaMaintenance.IsAreaUpdated')
    .readOnly()
    .build(),
  FieldBuilder
    .text('notes', 'Entity.StandAreaMaintenance.Notes')
    .maxLength(150)
    .build(),
  FieldBuilder.foreignKey(
    'periodId',
    'Entity.Period',
    PeriodService
  )
    .required()
    .display('name')
    .displayFromNested('periodName')
    .sortBy('periodName')
    .build(),
  FieldBuilder.foreignKey(
    'adjustmentReasonId',
    'Entity.StandAreaMaintenance.AdjustmentReason',
    ReasonService
  )
    .required()
    .display('name')
    .displayFromNested('adjustmentReasonName')
    .sortBy('adjustmentReasonName')
    .build(),
  FieldBuilder.foreignKey(
    'cycleId',
    'Entity.Cycle',
    CycleProjectionPlanningOrchestratorService
  )
    .display('cycleDisplay')
    .displayFromNested('cycleValue')
    .sortBy('cycleValue')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'rotationId',
    'Entity.Rotation',
    RotationProjectionPlanningOrchestratorService
  )
    .required()
    .display('rotationDisplay')
    .displayFromNested('rotationValue')
    .sortBy('rotationValue')
    .build(),
  FieldBuilder.foreignKey(
    'standLocationId',
    'Entity.StandAreaMaintenance.StandLocation',
    LocationService
  )
    .required()
    .display('name')
    .queryFilter({ locationTypeEntityName: 'Stand' })
    .displayFromNested('standName')
    .sortBy('standName')
    .build(),
  FieldBuilder.foreignKey(
    'costReceiverId',
    'Entity.CostReceiver',
    CostReceiverService,
  )
    .display('name')
    .displayFromNested('costReceiverName')
    .sortBy('costReceiverName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'standAreaMaintenanceTypeId',
    'Entity.StandAreaMaintenanceType',
    StandAreaMaintenanceTypeService
  )
    .display('name')
    .displayFromNested('standAreaMaintenanceTypeName')
    .sortBy('standAreaMaintenanceTypeName')
    .build(),
];

const namespace = ZendraLocalizationServices.Planning;
const namespaceGeographic = ZendraLocalizationServices.Geographic;

export const STANDAREAMAINTENANCE_ACTION_IDS = {
  createStandAreaMaintenance: 0,
  deleteStandAreaMaintenance: 1,
  updateAreaTrigger: 2,
  undoAreaUpdateTrigger: 3,
};

const ACTION_STANDAREAMAINTENANCE_WRAPPER: IModal = createFormModalConfig({
  component: ActionCreateStandAreaMaintenance,
  title: createLocalizationKey('Action.Modal.createStandAreaMaintenance', namespaceGeographic),
  subTitle: createLocalizationKey('Entity.StandAreaMaintenance', namespaceGeographic),
  size: 'xl',
  asideConfig: {
    show: true,
    title: createLocalizationKey('Entity.StandAreaMaintenance.StandName', namespaceGeographic),
    data: {},
  },
});

export const STANDAREAMAINTENANCE_ACTIONS_CONFIG: IActionsSchema = {
  overTable: [
    modalAction(
      STANDAREAMAINTENANCE_ACTION_IDS.createStandAreaMaintenance,
      'Action.Modal.createStandAreaMaintenance',
      ActionOverRecordTypes.OverTable,
      ACTION_STANDAREAMAINTENANCE_WRAPPER,
      namespaceGeographic
    ),
    triggerAction(
      STANDAREAMAINTENANCE_ACTION_IDS.updateAreaTrigger,
      'Action.Trigger.UpdateArea',
      ActionOverRecordTypes.OverTable,
      namespaceGeographic,
    ),
  ],
  multipleRecord: [
    triggerAction(
      STANDAREAMAINTENANCE_ACTION_IDS.undoAreaUpdateTrigger,
      'Action.Trigger.UndoAreaUpdate',
      ActionOverRecordTypes.MultipleRecord,
      namespaceGeographic,
    ),
  ],
  singleRecord: [
    triggerAction(
      STANDAREAMAINTENANCE_ACTION_IDS.deleteStandAreaMaintenance,
      'Action.Trigger.DeleteStandAreaMaintenance',
      ActionOverRecordTypes.SingleRecord,
      namespaceGeographic,
    ),
  ],
};

export const STANDAREAMAINTENANCE_CONFIG = {
  entityName: 'StandAreaMaintenance',
  localizationKey: 'Geographic::Entity.StandAreaMaintenance',
  fields: STANDAREAMAINTENANCE_FIELDS,
  permission: 'PlanningGeography.StandAreaMaintenances',
  actions: STANDAREAMAINTENANCE_ACTIONS_CONFIG,
  actionIds: STANDAREAMAINTENANCE_ACTION_IDS,
  showInMenu: false,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Planning + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.Geography + MENU_NAME_SUFFIX,
};
